// EditDocument.js
import React, { useState, useEffect } from 'react'
import { apiPut, apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import { useForm } from 'react-hook-form'
import useToastContext from 'hooks/useToastContext'
import { useTranslation } from 'react-i18next'
import OInputField from 'components/reusable/OInputField'
import OTextArea from 'components/reusable/OTextArea'
import OSelect from 'components/reusable/OSelect'
import { preventMaxInput } from 'utils/validations'
import formValidation from '../../utils/formValidation'

const EditDocument = ({ fetchDocuments, currentItem, handleEdit, user }) => {
  const { t } = useTranslation()
  const notification = useToastContext()
  const [isLoading, setIsLoading] = useState(false)
  const [categoryOptions, setCategoryOptions] = useState([])

  const yesNoOptions = [
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ]

  const {
    setValue,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true
  })

  useEffect(() => {
    if (currentItem) {
      reset({
        name: currentItem?.name,
        description: currentItem?.description,
        category: currentItem?.category,
        hasMandatoryExpiry: currentItem?.hasMandatoryExpiry,
        requiresElectronicSignature: currentItem?.requiresElectronicSignature
      })
    }
  }, [currentItem, reset])

  const onSubmit = async data => {
    try {
      setIsLoading(true)
      const payload = {
        name: data?.name?.trim(),
        description: data?.description?.trim(),
        category: data.category,
        hasMandatoryExpiry: data.hasMandatoryExpiry,
        requiresElectronicSignature: data.requiresElectronicSignature,
        companyID: user?.companyID || ''
      }
      const path = `${apiPath.documentUpdate}/${currentItem._id}`
      console.log('payload in EditDocument:', payload)
      const result = await apiPut(path, payload)
      if (result.data.success) {
        fetchDocuments()
        handleEdit(false)
        notification.success(result?.data.message)
      } else {
        notification.error(result?.data.message)
      }
    } catch (error) {
      console.log('error updating document:', error)
      notification.error(error.message || t('SOMETHING_WENT_WRONG'))
    } finally {
      setIsLoading(false)
    }
  }

   useEffect(() => {
    const fetchCategories = async () => {
      try {
        const payload = {
          page: 1,
          pageSize: 100,
          status: 'active'
        }
        const path = apiPath.categoryList
        const result = await apiGet(path, payload)
        const categories = result?.data?.results?.docs || []
        
        const options = categories.map(cat => ({
          value: cat._id,
          label: cat.name
        }))
        setCategoryOptions(options)
      } catch (error) {
        console.log('error fetching categories:', error.message)
      }
    }
    fetchCategories()
  }, [])

  return (
    <>
      <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto outline-none focus:outline-none fixed inset-0 z-50 '>
        <div className='relative w-auto mx-auto max-w-3xl my-6'>
          <div className='md:py-4 sm:py-8 px-7 sm:px-2'>
            <div className='shadow-lg relative flex flex-col w-full border-0 rounded-lg bg-white outline-none focus:outline-none min-w-[550px]'>
              <div className='justify-between p-5 border-b border-solid flex items-center border-slate-200 rounded-t'>
                <h3 className='font-semibold text-xl'>{t('EDIT_DOCUMENT')}</h3>
                <button
                  className='justify-center text-black border-2 rounded-full ml-auto flex items-center h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none'
                  onClick={() => handleEdit(false)}
                  title='Close'
                  disabled={isLoading}
                >
                  <span className='text-4xl text-[#B8BBBF]'>×</span>
                </button>
              </div>
              {/* Body */}
              <div className='p-6 relative flex-auto'>
                <div className='space-y-6'>
                  {/* Document Name */}
                  <div className='z-0 w-full group relative'>
                    <OInputField
                      name='name'
                      id='name'
                      autoFocus={true}
                      wrapperClassName='relative z-0 w-full group'
                      type='text'
                      inputLabel={
                        <>
                          {t('DOCUMENT_NAME')}
                          <span className='text-red-500'>*</span>
                        </>
                      }
                      // placeholder={t('ENTER_DOCUMENT_NAME')}
                      maxLength={100}
                      onInput={e => preventMaxInput(e, 100)}
                      register={register(
                        'name',
                        formValidation['documentName']
                      )}
                      errors={errors}
                    />
                  </div>

                  {/* Document Category */}
                  <div className='z-0 w-full group relative'>
                    <OSelect
                      name="category"
                      id="category"
                      wrapperClassName="relative z-0 w-full group"
                      inputLabel={
                        <>
                          {t("DOCUMENT_CATEGORY")}
                          <span className="text-red-500">*</span>
                        </>
                      }
                      options={categoryOptions}
                      value={watch('category')}
                      onChange={(value) => setValue('category', value)}
                      register={register('category', {
                        required: t('PLEASE_SELECT_CATEGORY')
                      })}
                      errors={errors}
                    />
                  </div>

                  {/* Document Description */}
                  <div className='z-0 w-full group relative'>
                    <OTextArea
                      name='description'
                      id='description'
                      wrapperClassName='relative z-0 w-full group'
                      inputLabel={t('DESCRIPTION')}
                      // placeholder={t('ENTER_DOCUMENT_DESCRIPTION')}
                      maxLength={500}
                      rows={4}
                      onInput={e => preventMaxInput(e, 500)}
                      register={register('description')}
                      errors={errors}
                    />
                  </div>

                  <div className='grid sm:grid-cols-2 gap-6'>
                    {/* Mandatory Expiry Date */}
                    {/* <div className='z-0 w-full group relative'>
                      <OSelect
                        name='hasMandatoryExpiry'
                        id='hasMandatoryExpiry'
                        wrapperClassName='relative z-0 w-full group'
                        inputLabel={
                          <>
                            {t('MANDATORY_EXPIRY_DATE')}
                            <span className='text-red-500'>*</span>
                          </>
                        }
                        options={yesNoOptions}
                        value={watch('hasMandatoryExpiry')}
                        onChange={(value) => setValue('hasMandatoryExpiry', value)}
                        register={register('hasMandatoryExpiry', {
                          required: t('PLEASE_SELECT_EXPIRY_OPTION')
                        })}
                        errors={errors}
                      />
                    </div> */}

                    {/* Electronic Signature Required */}
                    {/* <div className='z-0 w-full group relative'>
                      <OSelect
                        name='requiresElectronicSignature'
                        id='requiresElectronicSignature'
                        wrapperClassName='relative z-0 w-full group'
                        inputLabel={
                          <>
                            {t('ELECTRONIC_SIGNATURE_REQUIRED')}
                            <span className='text-red-500'>*</span>
                          </>
                        }
                        options={yesNoOptions}
                        value={watch('requiresElectronicSignature')}
                        onChange={(value) => setValue('requiresElectronicSignature', value)}
                        register={register('requiresElectronicSignature', {
                          required: t('PLEASE_SELECT_SIGNATURE_OPTION')
                        })}
                        errors={errors}
                      />
                    </div> */}
                  </div>
                </div>
              </div>
              {/* Footer */}
              <div className='border-t flex items-center justify-center p-6 border-solid border-slate-200 rounded-b'>
                <button
                  onClick={() => handleEdit(false)}
                  className='text-black bg-[#E1E1E1] focus:outline-none rounded mr-6 font-normal px-12 py-2.5 text-sm outline-none ease-linear transition-all duration-150'
                  type='button'
                  disabled={isLoading}
                >
                  {t('O_BACK')}
                </button>
                <button
                  type='submit'
                  className='active:bg-emerald-600 px-8 py-2.5 font-normal text-sm bg-LightBlue text-white rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150'
                  onClick={handleSubmit(onSubmit)}
                  disabled={isLoading}
                >
                  {isLoading ? t('SAVING...') : t('O_SAVE')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='fixed inset-0 opacity-25 z-40 bg-black' />
    </>
  )
}

export default EditDocument